import React, { Suspense, lazy } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Box, Eye, BarChart3, Briefcase, Users, FileText } from 'lucide-react';

// Lazy load components to reduce initial bundle size
const RealProgressDashboard = lazy(() => import('@/components/progress/RealProgressDashboard').then(m => ({ default: m.RealProgressDashboard })));
const Interactive3DModel = lazy(() => import('@/components/visualization/Interactive3DModel').then(m => ({ default: m.Interactive3DModel })));
const HighContrastMode = lazy(() => import('@/components/accessibility/HighContrastMode').then(m => ({ default: m.HighContrastMode })));
const AudioFirstLearning = lazy(() => import('@/components/accessibility/AudioFirstLearning').then(m => ({ default: m.AudioFirstLearning })));
const AccessibilityPanel = lazy(() => import('@/components/accessibility/AccessibilityPanel').then(m => ({ default: m.AccessibilityPanel })));
const LearningAnalyticsDashboard = lazy(() => import('@/components/analytics/LearningAnalyticsDashboard').then(m => ({ default: m.LearningAnalyticsDashboard })));
const InterviewPractice = lazy(() => import('@/components/career/InterviewPractice'));
const EnhancedResumeBuilder = lazy(() => import('@/components/career/EnhancedResumeBuilder').then(m => ({ default: m.EnhancedResumeBuilder })));

// Error boundary for Interview Practice
import { InterviewPracticeErrorBoundary } from '@/components/error/InterviewPracticeErrorBoundary';

// Loading component
const ComponentLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

export const AdvancedFeatures: React.FC = () => {
  return (
    <div className="container mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Advanced Features</h1>
        <p className="text-muted-foreground">
          Explore our cutting-edge learning tools and features designed to enhance your educational experience.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Interview Prep
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Practice 16,000+ interview questions across 50+ job roles. 
              Mobile-optimized with smart filters.
            </p>
          </CardContent>
        </Card>
        
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Resume Builder
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Create professional resumes with 8+ templates. Export to PDF/Markdown.
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="progress" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6">
          <TabsTrigger value="progress" className="flex items-center gap-2 text-xs">
            <Trophy className="h-4 w-4" />
            Progress
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2 text-xs">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="interview" className="flex items-center gap-2 text-xs">
            <Briefcase className="h-4 w-4" />
            Interview Prep
          </TabsTrigger>
          <TabsTrigger value="resume" className="flex items-center gap-2 text-xs">
            <FileText className="h-4 w-4" />
            Resume
          </TabsTrigger>
          <TabsTrigger value="3d-models" className="flex items-center gap-2 text-xs">
            <Box className="h-4 w-4" />
            3D Models
          </TabsTrigger>
          <TabsTrigger value="accessibility" className="flex items-center gap-2 text-xs">
            <Eye className="h-4 w-4" />
            Accessibility
          </TabsTrigger>
        </TabsList>

        <TabsContent value="progress" className="space-y-6">
          <Suspense fallback={<ComponentLoader />}>
            <RealProgressDashboard />
          </Suspense>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <Suspense fallback={<ComponentLoader />}>
            <LearningAnalyticsDashboard />
          </Suspense>
        </TabsContent>

        <TabsContent value="interview" className="space-y-6">
          <InterviewPracticeErrorBoundary>
            <Suspense fallback={<ComponentLoader />}>
              <InterviewPractice />
            </Suspense>
          </InterviewPracticeErrorBoundary>
        </TabsContent>

        <TabsContent value="resume" className="space-y-6">
          <Suspense fallback={<ComponentLoader />}>
            <EnhancedResumeBuilder />
          </Suspense>
        </TabsContent>

        <TabsContent value="3d-models" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Suspense fallback={<ComponentLoader />}>
              <Interactive3DModel topic="React" />
            </Suspense>
            <Suspense fallback={<ComponentLoader />}>
              <Interactive3DModel topic="JavaScript" />
            </Suspense>
          </div>
        </TabsContent>

        <TabsContent value="accessibility" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            <Suspense fallback={<ComponentLoader />}>
              <HighContrastMode />
            </Suspense>
            <Suspense fallback={<ComponentLoader />}>
              <AudioFirstLearning />
            </Suspense>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Accessibility Features</h3>
            <p className="text-muted-foreground mb-4">
              Our platform includes comprehensive accessibility features to ensure everyone can learn effectively. 
              Look for the floating accessibility button in the bottom-right corner to customize your experience.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">High contrast mode</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Adjustable font sizes</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Screen reader support</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Keyboard navigation</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Color blind support</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Audio-first learning</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Global Accessibility Panel */}
      <Suspense fallback={null}>
        <AccessibilityPanel />
      </Suspense>
    </div>
  );
};